import { type CSSResultGroup, html, LitElement, type PropertyValues, unsafeCSS } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { ContextConsumer } from '@lit/context'
import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom'
import { emit, waitForEvent, time, createFocusTrapSentinels } from '@/navbar/utils'
import {
  isArrowDown,
  isArrowUp,
  isHome,
  isEnd,
  isEscape,
  isEnter,
  isTab,
  isSpace,
} from '@/navbar/utils/eventKeys'
import { navbarContext, type NavbarPart } from '@/navbar/components/context'
import { $t, localized } from '@/navbar/i18n'
import type { Icon, MenuItem } from '@/navbar/navbar.types'

// Types/local class imports:
import { EcoSuiteNavbar } from '../eco-suite-navbar.component'
import { type EcoSuiteNavbarButton } from '../button/eco-suite-navbar-button.component'
import { EcoSuiteNavbarMenuItem } from '../menu-item/eco-suite-navbar-menu-item.component'
import { EcoSuiteNavbarAppSwitcher } from '../app-switcher/eco-suite-navbar-app-switcher.component'

// Styles:
import styles from './eco-suite-navbar-menu.scss?inline'

// Child components:
import '../button/eco-suite-navbar-button.component'
import '../menu-header/eco-suite-navbar-menu-header.component'
import '../menu-item/eco-suite-navbar-menu-item.component'

/**
 * @summary A dropdown menu triggered by a navbar button, supporting both desktop and mobile layouts.
 * @tag eco-suite-navbar-menu
 * @slot button - Custom trigger button, replacing the default navbar button.
 * @slot [default] - Menu items and content rendered inside the dropdown panel.
 * @event eco-show - Emitted when the menu begins to open.
 * @event eco-after-show - Emitted after the menu has fully opened.
 * @event eco-hide - Emitted when the menu begins to close.
 * @event eco-after-hide - Emitted after the menu has fully closed.
 */
@localized()
@customElement('eco-suite-navbar-menu')
export class EcoSuiteNavbarMenu extends LitElement {
  /** @internal */ static readonly styles: CSSResultGroup = [unsafeCSS(styles)]
  /** @internal */ static readonly shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true }
  /** @internal */ private static _instanceCounter = 0
  /** @internal */ private readonly _instanceId = ++EcoSuiteNavbarMenu._instanceCounter
  /** @internal */ private navbarContextConsumer?: ContextConsumer<{ __context__: NavbarPart }, this>
  /** @internal */ private closeWatcher?: CloseWatcher | null
  /** @internal */ private positionCleanup?: () => void

  /** @internal */
  private readonly _mobileSentinelHandlers = createFocusTrapSentinels(
    () => this.focusFirstMobileElement(),
    () => this.focusLastMobileElement(),
  )

  /** @internal */ private readonly handleSentinelStartFocus = this._mobileSentinelHandlers.start
  /** @internal */ private readonly handleSentinelEndFocus = this._mobileSentinelHandlers.end

  /** @internal */
  @query('.EcoSuiteNavbarMenu__dropdown')
  private readonly dropdown?: HTMLDivElement

  /** @internal */
  @query('eco-suite-navbar-button')
  private readonly button?: EcoSuiteNavbarButton

  /** @internal */
  @query('.EcoSuiteNavbarMenu__button')
  private readonly buttonSlot?: HTMLSlotElement

  /** @internal */
  @query('.EcoSuiteNavbarMenu__mobilePanel')
  private readonly mobilePanel?: HTMLElement

  /** @internal */
  @query('slot:not([name])')
  private readonly defaultSlot?: HTMLSlotElement

  /** Menu button icon. */
  @property()
  icon?: Icon

  /** Menu label. */
  @property()
  label?: string

  /** Tooltip text to use if the label alone is not sufficient. */
  @property()
  tooltip?: string

  /** Display the label regardless of screen size. */
  @property({ type: Boolean, attribute: 'show-label' })
  showLabel = false

  /** Hide the header in the dropdown menu. */
  @property({ type: Boolean, attribute: 'hide-menu-header' })
  hideMenuHeader = false

  /** Items to display in the dropdown menu. */
  @property({ type: Array, attribute: 'menu-items' })
  menuItems?: MenuItem[]

  /** Display a green dot to the top-right of the button icon. */
  @property({ type: Boolean, attribute: 'has-badge' })
  hasBadge = false

  /**
   * @internal
   * Override the inherited navbar context to force the menu to appear with certain styles.
   * */
  @property({ attribute: false })
  forceNavbarContext?: NavbarPart

  /** @internal */
  @property({ type: Boolean, attribute: false })
  isAppSwitcher = false

  /** @internal */
  @state()
  private isOpen = false

  /** @internal */
  @state()
  private dropdownPositioned = false

  /** @internal */
  get _dropdownId() {
    return `eco-suite-navbar-menu-dropdown-${this._instanceId}`
  }

  /** @internal */
  get navbarContext() {
    return this.forceNavbarContext ?? this.navbarContextConsumer?.value
  }

  async connectedCallback() {
    super.connectedCallback()
    await this.updateComplete

    this.navbarContextConsumer = new ContextConsumer(this, {
      context: navbarContext,
      subscribe: true,
      callback: () => {
        if (this.navbarContext === 'desktop') {
          this.setCurrentItem()
        } else if (this.navbarContext === 'mobile') {
          this.clearCurrentItem()
        }
      },
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeOpenListeners()
    this.stopAutoPositioning()
    this.isOpen = false
  }

  protected willUpdate(changedProps: PropertyValues) {
    if (changedProps.has('isOpen') && this.hasUpdated) {
      this.handleOpenChange()
    }
  }

  protected updated(changedProps: PropertyValues) {
    super.updated(changedProps)

    if (changedProps.has('isOpen')) {
      // Keep ARIA state on any slotted custom trigger in sync with `isOpen`.
      this.syncSlottedButtonAria()
    }
  }

  /** @internal */
  private handleButtonSlotChange() {
    this.syncSlottedButtonAria()
  }

  /** @internal */
  private syncSlottedButtonAria() {
    const slotted = this.buttonSlot?.assignedElements({ flatten: true })[0] as HTMLElement | undefined
    if (!slotted) return

    slotted.setAttribute('aria-haspopup', 'menu')
    slotted.setAttribute('aria-controls', this._dropdownId)
    slotted.setAttribute('aria-expanded', String(this.isOpen))
  }

  /** Focus the menu button. */
  public focusButton() {
    const button = this.buttonSlot?.assignedElements({ flatten: true })[0] as (HTMLElement | undefined) ?? this.button
    button?.focus?.()
  }

  /** @internal */
  private handleDesktopDropdownKeydown(event: KeyboardEvent) {
    if (isEscape(event) && !this.closeWatcher) {
      event.stopPropagation()
      this.close(true)
      return
    }

    if (isTab(event) && !this.isAppSwitcher) {
      event.preventDefault()
      this.close(true)
      return
    }

    if (isArrowDown(event) || isArrowUp(event) || isHome(event) || isEnd(event)) {
      // TODO: make this reusable for app switcher as well
      const items = this.getAllItems()
      const activeItem = this.getCurrentItem()
      let index = activeItem ? items.indexOf(activeItem) : 0

      if (items.length) {
        event.preventDefault()
        event.stopPropagation()

        if (isArrowDown(event)) {
          index++
        } else if (isArrowUp(event)) {
          index--
        } else if (isHome(event)) {
          index = 0
        } else if (isEnd(event)) {
          index = items.length - 1
        }

        index = this.wrapIndex(index, items.length)
        this.setCurrentItem(items[index])
        items[index].focus()
      }
    }
  }

  /** @internal */
  private readonly handleDocumentKeydown = async (event: KeyboardEvent) => {
    if (isEscape(event) && this.isOpen && !this.closeWatcher) {
      event.preventDefault()
      return this.close(true)
    }

    if (this.navbarContext === 'mobile') return

    if (isTab(event)) {
      // For non-app-switcher menus, Tab is already intercepted and prevented
      // in handleDesktopDropdownKeydown, which closes the menu before focus moves.
      // This branch exists solely for the app-switcher, where Tab is allowed
      // to move focus freely between items; we wait for focus to settle, then
      // close if it has left the navbar entirely.
      if (!this.isAppSwitcher) return

      await time()
      const activeElement = document.activeElement as HTMLElement

      if (activeElement?.tagName.toLowerCase() === EcoSuiteNavbar.tagName) {
        const activeMenu = activeElement.shadowRoot?.activeElement as HTMLElement | null
        const appSwitcherActive = activeMenu?.tagName.toLowerCase() === EcoSuiteNavbarAppSwitcher.tagName
        if (!appSwitcherActive) this.close()
      } else {
        this.close(true)
      }
    }
  }

  /** @internal */
  private readonly handleDocumentMousedown = (event: MouseEvent) => {
    const path = event.composedPath()
    if (!path.includes(this)) this.close()
  }

  /** @internal */
  private async handleButtonClick() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  /** @internal */
  private async handleButtonKeydown(event: KeyboardEvent) {
    if (isSpace(event) || isEnter(event)) {
      this.handleButtonClick()
      return
    }

    if (this.navbarContext === 'mobile') return

    if (isArrowDown(event) || isArrowUp(event) || isHome(event) || isEnd(event)) {
      event.preventDefault()

      const menuItems = this.getAllItems()
      const firstMenuItem = menuItems[0]
      const lastMenuItem = menuItems[menuItems.length - 1]

      if (!this.isOpen) this.open()
      if (!menuItems.length) return
      await this.updateComplete

      if (isArrowDown(event) || isHome(event)) {
        this.setCurrentItem(firstMenuItem)
        firstMenuItem.focus()
      }

      if (isArrowUp(event) || isEnd(event)) {
        this.setCurrentItem(lastMenuItem)
        lastMenuItem.focus()
      }
    }
  }

  /** @internal */
  private handleButtonKeyup(event: KeyboardEvent) {
    // Prevent space from triggering an additional click event
    if (isSpace(event)) event.preventDefault()
  }

  /**
   * Open the menu.
   */
  public async open() {
    if (this.isOpen) return undefined
    this.isOpen = true
    return waitForEvent(this, 'eco-after-show')
  }

  /**
   * Close the menu.
   * @param focusButton Whether to return focus to the menu button after closing. Defaults to `false`.
   */
  public async close(focusButton = false) {
    if (!this.isOpen) return undefined
    this.isOpen = false
    if (focusButton) this.focusButton()
    return await waitForEvent(this, 'eco-after-hide')
  }

  /** @internal */
  private addOpenListeners() {
    if ('CloseWatcher' in globalThis) {
      this.closeWatcher?.destroy()
      this.closeWatcher = new CloseWatcher()
      this.closeWatcher.onclose = () => this.close(true)
    }
    document.addEventListener('keydown', this.handleDocumentKeydown)
    document.addEventListener('mousedown', this.handleDocumentMousedown)
  }

  /** @internal */
  private removeOpenListeners() {
    document.removeEventListener('keydown', this.handleDocumentKeydown)
    document.removeEventListener('mousedown', this.handleDocumentMousedown)
    this.closeWatcher?.destroy()
  }

  /** @internal */
  private isMenuItem(item: HTMLElement) {
    return item.tagName.toLowerCase() === EcoSuiteNavbarMenuItem.tagName
  }

  /** @internal */
  private getAllItems() {
    if (this.menuItems?.length) {
      return Array.from(this.shadowRoot!.querySelectorAll('eco-suite-navbar-menu-item'))
    }

    const assignedElements = this.defaultSlot?.assignedElements({ flatten: true }) as HTMLElement[]
    const items: EcoSuiteNavbarMenuItem[] = []
    assignedElements?.forEach((el: HTMLElement) => {
      // Detect wrapper elements whose `id` follows the framework convention
      // `<something>-slot-<something>` (e.g. Vue/Angular slot wrappers). When
      // present, look one level deeper through the wrapper's own `<slot>` to
      // find the actual menu items.
      if (/-slot-/.exec(el.id)) {
        const innerSlot = el.querySelector('slot')
        if (!innerSlot) return
        const innerAssignedElements = innerSlot.assignedElements({ flatten: true }) as HTMLElement[]
        items.push(...innerAssignedElements as EcoSuiteNavbarMenuItem[])
      } else if (!el.inert && this.isMenuItem(el)) {
        items.push(el as EcoSuiteNavbarMenuItem)
      }
    })
    return items
  }

  /**
   * Gets the current menu item, which is the menu item that has `tabindex="0"` within the roving tab index.
   * The menu item may or may not have focus, but for keyboard interaction purposes it's considered the "active" item.
   */
  /** @internal */
  private getCurrentItem() {
    return this.getAllItems().find(i => i.getAttribute('tabindex') === '0')
  }

  /**
   * @internal
   * Desktop only:
   * Sets the current menu item to the specified element. This sets `tabindex="0"` on the target element and
   * `tabindex="-1"` to all other items. This method must be called prior to setting focus on a menu item.
   */
  private setCurrentItem(item?: EcoSuiteNavbarMenuItem) {
    const items = this.getAllItems()
    item ||= items[0]

    items.forEach(i => {
      i.setAttribute('tabindex', i === item ? '0' : '-1')
    })
  }

  /**
   * @internal
   * On mobile, clear any -1 tabindexes that may have been set for the desktop view.
   * */
  private async clearCurrentItem() {
    await this.updateComplete
    const items = this.getAllItems()

    items.forEach(i => {
      i.setAttribute('tabindex', '0')
    })
  }

  /** Wraps `index` within `[0, length)`, cycling past either end. @internal */
  private wrapIndex(index: number, length: number): number {
    if (index < 0) return length - 1
    if (index >= length) return 0
    return index
  }

  /**
   * Returns focusable elements inside the mobile panel, including both
   * shadow-DOM descendants (Back button, `menuItems`-driven menu items) and
   * slotted light-DOM descendants (consumer-provided menu items, links,
   * buttons, inputs). Used to drive the mobile focus trap; the first entry
   * receives focus on open, and the start/end sentinels wrap to the last/first
   * focusable respectively.
   * @internal
   */
  private getMobileFocusableElements(): HTMLElement[] {
    const panel = this.mobilePanel
    if (!panel) return []

    const selector = [
      'eco-suite-navbar-menu-item',
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
    ].join(',')

    const shadowMatches = Array.from(panel.querySelectorAll<HTMLElement>(selector))

    // Slotted (light-DOM) descendants aren't reachable via the shadow
    // querySelectorAll above; collect them via assignedElements and include
    // their own matching descendants.
    const defaultSlot = panel.querySelector<HTMLSlotElement>('slot')
    const lightMatches: HTMLElement[] = []
    if (defaultSlot) {
      const assigned = defaultSlot.assignedElements({ flatten: true }) as HTMLElement[]
      for (const node of assigned) {
        if (node.matches?.(selector)) lightMatches.push(node)
        lightMatches.push(...Array.from(node.querySelectorAll<HTMLElement>(selector)))
      }
    }

    return [...shadowMatches, ...lightMatches]
  }

  /** @internal */
  private focusFirstMobileElement(): void {
    this.getMobileFocusableElements()[0]?.focus()
  }

  /** @internal */
  private focusLastMobileElement(): void {
    const focusables = this.getMobileFocusableElements()
    focusables[focusables.length - 1]?.focus()
  }

  /** @internal */
  private async handleOpenChange() {
    if (this.isOpen) {
      emit(this, 'eco-show')
      this.addOpenListeners()

      if (this.navbarContext === 'desktop') {
        await this.updateComplete
        this.startAutoPositioning()
      }

      emit(this, 'eco-after-show')
    } else {
      emit(this, 'eco-hide')
      this.removeOpenListeners()
      this.stopAutoPositioning()
      emit(this, 'eco-after-hide')
    }
  }

  /**
   * Subscribes the dropdown to its anchor via Floating UI's `autoUpdate`, so
   * the dropdown follows the trigger on scroll, resize, and layout shifts
   * while it is open.
   * @internal
   */
  private startAutoPositioning() {
    const anchor = this.buttonSlot?.assignedElements({ flatten: true })[0] as HTMLElement ?? this.button
    if (!anchor || !this.dropdown) return

    this.stopAutoPositioning()
    this.positionCleanup = autoUpdate(anchor, this.dropdown, () => {
      void this.positionDropdown(anchor)
    })
  }

  /** @internal */
  private stopAutoPositioning() {
    this.positionCleanup?.()
    this.positionCleanup = undefined
  }

  /** @internal */
  private async positionDropdown(anchor: HTMLElement) {
    if (!anchor || !this.dropdown) return

    const { x, y } = await computePosition(anchor, this.dropdown, {
      placement: 'bottom-end',
      middleware: [
        offset(6),
        flip(),
        shift({ padding: 8 }),
      ],
    })

    Object.assign(this.dropdown.style, {
      left: `${x}px`,
      top: `${y}px`,
    })

    this.dropdownPositioned = true
  }

  render() {
    if (this.navbarContext === 'mobile') return this.renderMobileView()
    if (this.navbarContext === 'desktop') return this.renderDesktopView()
    return ''
  }

  /** @internal */
  private renderDesktopView() {
    return html`
      <div
        class=${classMap({
          EcoSuiteNavbarMenu: true,
          'EcoSuiteNavbarMenu--desktop': true,
          'EcoSuiteNavbarMenu--open': this.isOpen,
          'EcoSuiteNavbarMenu--noHeader': !!this.hideMenuHeader,
          'EcoSuiteNavbarMenu--appSwitcher': !!this.isAppSwitcher,
        })}
      >
        <slot
          name="button"
          class="EcoSuiteNavbarMenu__button"
          @click=${this.handleButtonClick}
          @keydown=${this.handleButtonKeydown}
          @keyup=${this.handleButtonKeyup}
          @slotchange=${this.handleButtonSlotChange}
        >
          <eco-suite-navbar-button
            .icon=${this.icon}
            .label=${this.label}
            .tooltip=${this.tooltip}
            .showLabel=${this.showLabel}
            .isActive=${this.isOpen}
            .hasBadge=${this.hasBadge}
            .hasMenu=${true}
            .forceNavbarContext=${this.forceNavbarContext}
            data-qa-id="button"
            aria-haspopup="menu"
            aria-controls=${this._dropdownId}
          ></eco-suite-navbar-button>
        </slot>

        <div
          role="menu"
          id=${this._dropdownId}
          class="EcoSuiteNavbarMenu__dropdown"
          class=${classMap({
            'EcoSuiteNavbarMenu__dropdown': true,
            'EcoSuiteNavbarMenu__dropdown--hidden': !this.dropdownPositioned,
          })}
          aria-label=${ifDefined(this.label)}
          @keydown=${this.handleDesktopDropdownKeydown}
          @eco-click=${() => this.close()}
        >
          <eco-suite-navbar-menu-header>
            ${this.label}
          </eco-suite-navbar-menu-header>
          ${this.menuItems?.map(item => html`
            <eco-suite-navbar-menu-item
              .icon=${item.icon}
              .label=${item.label}
              .href=${item.href}
              .isCurrent=${item.isCurrent}
              .target=${item.target}
              .isExternal=${item.isExternal}
              .onClick=${item.onClick}
              data-qa-id=${ifDefined(item.dataQaId)}
            ></eco-suite-navbar-menu-item>
          `)}
          <slot
            style=${this.menuItems?.length ? 'display: none' : ''}
            @slotchange=${() => this.setCurrentItem()}
          ></slot>
        </div>
      </div>
    `
  }

  /** @internal */
  private renderMobileView() {
    return html`
      <slot
        name="button"
        class="EcoSuiteNavbarMenu__button"
        @click=${this.handleButtonClick}
        @keydown=${this.handleButtonKeydown}
        @keyup=${this.handleButtonKeyup}
        @slotchange=${this.handleButtonSlotChange}
      >
        <eco-suite-navbar-button
          .icon=${this.icon}
          .label=${this.label}
          .tooltip=${this.tooltip}
          .showLabel=${this.showLabel}
          .isActive=${this.isOpen}
          .hasBadge=${this.hasBadge}
          .hasMenu=${true}
          .forceNavbarContext=${this.forceNavbarContext}
          data-qa-id="button"
          aria-haspopup="menu"
          aria-controls=${this._dropdownId}
        ></eco-suite-navbar-button>
      </slot>

      <div class="EcoSuiteNavbarMenu">
        <div
          role="menu"
          id=${this._dropdownId}
          class=${classMap({
            'EcoSuiteNavbarMenu__mobilePanel': true,
            'EcoSuiteNavbarMenu__mobilePanel--open': this.isOpen,
            'EcoSuiteNavbarMenu--noHeader': !!this.hideMenuHeader,
          })}
          aria-label=${ifDefined(this.label)}
          aria-hidden=${this.isOpen ? 'false' : 'true'}
          ?inert=${!this.isOpen}
        >
          <span
            class="EcoSuiteNavbarMenu__sentinelStart"
            tabindex=${this.isOpen ? '0' : '-1'}
            aria-hidden="true"
            @focus=${this.handleSentinelStartFocus}
          ></span>
          <button
            class="EcoSuiteNavbarMenu__backBtn"
            type="button"
            @click=${() => this.close(true)}
          >
            <pwc-icon-shadow icon="chevron-left" size="xs" aria-hidden="true"></pwc-icon-shadow>
            <span>${$t('navbar.back')}</span>
          </button>
          <eco-suite-navbar-menu-header>
            ${this.label}
          </eco-suite-navbar-menu-header>
          ${this.menuItems?.map(item => html`
            <eco-suite-navbar-menu-item
              .icon=${item.icon}
              .label=${item.label}
              .href=${item.href}
              .isCurrent=${item.isCurrent}
              .target=${item.target}
              .isExternal=${item.isExternal}
              .onClick=${item.onClick}
              data-qa-id=${ifDefined(item.dataQaId)}
            ></eco-suite-navbar-menu-item>
          `)}
          <slot style=${this.menuItems?.length ? 'display: none' : ''}></slot>
          <span
            class="EcoSuiteNavbarMenu__sentinelEnd"
            tabindex=${this.isOpen ? '0' : '-1'}
            aria-hidden="true"
            @focus=${this.handleSentinelEndFocus}
          ></span>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar-menu': EcoSuiteNavbarMenu
  }
}
